import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const BlogPost: React.FC = () => {
  const { t } = useTranslation();
  const postId = window.location.pathname.split("/blog/")[1];
  
  // Sample blog posts data - in a real app, this would be fetched from an API
  const blogPosts = {
    "cbam-impact": {
      title: t("blog.posts.cbam.title") || "Impact du CBAM sur les entreprises exportatrices",
      summary: t("blog.posts.cbam.summary") || "Analyse des conséquences du mécanisme d'ajustement carbone aux frontières pour les exportateurs",
      category: "regulation",
      date: "2025-05-15",
      image: "/images/blog/cbam.jpg",
      tags: ["CBAM", "Exportation", "Réglementation"],
      content: [
        t("blog.posts.cbam.content.p1") || "Le mécanisme d'ajustement carbone aux frontières (CBAM) représente un changement majeur pour les entreprises exportatrices.",
        t("blog.posts.cbam.content.p2") || "Pour les entreprises exportatrices, le CBAM implique de nouvelles obligations de reporting et potentiellement des coûts supplémentaires.",
        t("blog.posts.cbam.content.p3") || "La préparation au CBAM nécessite une compréhension approfondie de l'empreinte carbone de vos produits et de votre chaîne d'approvisionnement.",
        t("blog.posts.cbam.content.p4") || "Au-delà de la conformité, le CBAM peut également représenter une opportunité pour les entreprises qui ont déjà investi dans des processus de production bas carbone.",
        t("blog.posts.cbam.content.p5") || "Mypa propose un accompagnement spécifique pour les entreprises concernées par le CBAM."
      ]
    },
    "decarbonization-strategies": {
      title: t("blog.posts.strategies.title") || "Stratégies de décarbonation efficaces pour les PME",
      summary: t("blog.posts.strategies.summary") || "Guide pratique pour mettre en œuvre des actions de réduction d'empreinte carbone adaptées aux PME",
      category: "strategy",
      date: "2025-05-10",
      image: "/images/blog/strategy.jpg",
      tags: ["PME", "Stratégie", "Décarbonation"],
      content: [
        t("blog.posts.strategies.content.p1") || "La décarbonation représente un défi particulier pour les PME, qui disposent souvent de ressources limitées.",
        t("blog.posts.strategies.content.p2") || "La première étape consiste à réaliser un bilan carbone simplifié pour identifier les principales sources d'émissions.",
        t("blog.posts.strategies.content.p3") || "Des actions à gain rapide peuvent être mises en place rapidement.",
        t("blog.posts.strategies.content.p4") || "Pour aller plus loin, les PME peuvent envisager des investissements plus structurants.",
        t("blog.posts.strategies.content.p5") || "L'engagement des collaborateurs est un facteur clé de succès."
      ]
    },
    "climate-financing": {
      title: t("blog.posts.financing.title") || "Financement des projets climat : opportunités et mécanismes",
      summary: t("blog.posts.financing.summary") || "Panorama des solutions de financement disponibles pour les projets de décarbonation",
      category: "financing",
      date: "2025-05-05",
      image: "/images/blog/financing.jpg",
      tags: ["Financement", "Investissement", "Projets climat"],
      content: [
        t("blog.posts.financing.content.p1") || "Le financement constitue souvent un frein majeur à la mise en œuvre de projets de décarbonation ambitieux.",
        t("blog.posts.financing.content.p2") || "Les aides publiques se déclinent à différents niveaux.",
        t("blog.posts.financing.content.p3") || "Du côté des financements privés, on observe une multiplication des offres bancaires dédiées à la transition écologique.",
        t("blog.posts.financing.content.p4") || "Des mécanismes innovants émergent également.",
        t("blog.posts.financing.content.p5") || "La valorisation des réductions d'émissions carbone peut constituer une source de revenus complémentaire."
      ]
    },
    "textile-case-study": {
      title: t("blog.posts.case_study.title") || "Étude de cas : Transformation écologique d'une entreprise textile",
      summary: t("blog.posts.case_study.summary") || "Analyse d'un cas concret de décarbonation réussie dans le secteur textile",
      category: "case_study",
      date: "2025-04-28",
      image: "/images/blog/case-study.jpg",
      tags: ["Textile", "Étude de cas", "Transformation"],
      content: [
        t("blog.posts.case_study.content.p1") || "L'industrie textile figure parmi les secteurs les plus polluants au monde.",
        t("blog.posts.case_study.content.p2") || "Le point de départ a été la réalisation d'un bilan carbone complet.",
        t("blog.posts.case_study.content.p3") || "La première phase du plan d'action a ciblé l'approvisionnement.",
        t("blog.posts.case_study.content.p4") || "Pour la logistique, l'entreprise a revu l'ensemble de sa chaîne d'approvisionnement.",
        t("blog.posts.case_study.content.p5") || "Les résultats sont probants : en trois ans, l'entreprise a réduit son empreinte carbone de 45%."
      ]
    },
    "carbon-innovations": {
      title: t("blog.posts.innovation.title") || "Innovations dans la mesure et la réduction de l'empreinte carbone",
      summary: t("blog.posts.innovation.summary") || "Les dernières avancées technologiques au service de la décarbonation",
      category: "innovation",
      date: "2025-04-20",
      image: "/images/blog/innovation.jpg",
      tags: ["Innovation", "Technologie", "Mesure carbone"],
      content: [
        t("blog.posts.innovation.content.p1") || "Le domaine de la mesure et de la réduction de l'empreinte carbone connaît une véritable effervescence d'innovations.",
        t("blog.posts.innovation.content.p2") || "En matière de mesure, l'intelligence artificielle révolutionne la collecte et l'analyse des données d'émissions.",
        t("blog.posts.innovation.content.p3") || "Les capteurs IoT permettent désormais un suivi en temps réel des consommations énergétiques.",
        t("blog.posts.innovation.content.p4") || "Du côté de la réduction des émissions, les technologies de capture et stockage du carbone progressent rapidement.",
        t("blog.posts.innovation.content.p5") || "Les plateformes collaboratives facilitent le partage de bonnes pratiques et l'émergence de solutions innovantes."
      ]
    },
    "carbon-market-trends": {
      title: t("blog.posts.market.title") || "Tendances du marché carbone : opportunités et défis",
      summary: t("blog.posts.market.summary") || "Analyse des évolutions récentes du marché carbone et perspectives pour les entreprises",
      category: "market",
      date: "2025-04-15",
      image: "/images/blog/market.jpg",
      tags: ["Marché carbone", "Tendances", "Opportunités"],
      content: [
        t("blog.posts.market.content.p1") || "Le marché du carbone connaît une croissance exponentielle, porté par le renforcement des politiques climatiques.",
        t("blog.posts.market.content.p2") || "Les prix du carbone ont atteint des niveaux historiques, incitant les entreprises à accélérer leur décarbonation.",
        t("blog.posts.market.content.p3") || "De nouveaux mécanismes de marché émergent, offrant davantage de flexibilité aux acteurs économiques.",
        t("blog.posts.market.content.p4") || "Les marchés volontaires se structurent progressivement, avec des exigences accrues en matière de qualité des crédits carbone.",
        t("blog.posts.market.content.p5") || "Pour les entreprises, la compréhension de ces mécanismes devient un enjeu stratégique majeur."
      ]
    }
  };
  
  const post = blogPosts[postId as keyof typeof blogPosts];
  
  // Format date to localized string
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  };
  
  // Get related posts (same category, excluding current post)
  const getRelatedPosts = () => {
    if (!post) return [];
    
    return Object.entries(blogPosts)
      .filter(([id, blogPost]) => id !== postId && blogPost.category === post.category)
      .map(([id, blogPost]) => ({
        id,
        ...blogPost
      }))
      .slice(0, 2);
  };

  if (!post) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-mypa-blue mb-4">{t("blog.post_not_found.title") || "Article non trouvé"}</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">{t("blog.post_not_found.message") || "L'article que vous recherchez n'existe pas ou a été déplacé."}</p>
          <Link to="/blog" className="inline-block mt-6 bg-mypa-green text-white px-6 py-3 rounded-lg font-medium hover:bg-opacity-90 transition-colors">
            {t("blog.back_to_blog") || "Retour au blog"}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-12">
      {/* Back to blog link */}
      <div className="mb-8">
        <Link to="/blog" className="flex items-center text-mypa-blue hover:text-mypa-green transition-colors">
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
          </svg>
          {t("blog.back_to_blog") || "Retour au blog"}
        </Link>
      </div>
      
      {/* Article header */}
      <div className="mb-8">
        <div className="flex items-center text-sm text-gray-500 mb-4">
          <span>{formatDate(post.date)}</span>
          <span className="mx-2">•</span>
          <span className="capitalize">{t(`blog.categories.${post.category}`) || post.category}</span>
        </div>
        <h1 className="text-4xl font-bold text-mypa-blue mb-4">{post.title}</h1>
        <p className="text-xl text-gray-600">{post.summary}</p>
      </div>
      
      {/* Featured image */}
      <div className="mb-12 h-96 bg-gray-200 rounded-lg overflow-hidden">
        {/* Placeholder for post image */}
        <div className="w-full h-full flex items-center justify-center bg-mypa-blue bg-opacity-10">
          <span className="text-mypa-blue font-medium">{post.category}</span>
        </div>
      </div>
      
      {/* Article content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div className="prose prose-lg max-w-none">
            {post.content.map((paragraph, index) => (
              <p key={index} className="mb-6 text-gray-700">{paragraph}</p>
            ))}
          </div>
          
          {/* Tags */}
          <div className="mt-8 mb-12">
            <h3 className="text-lg font-bold text-mypa-blue mb-3">{t("blog.tags") || "Tags"}</h3>
            <div className="flex flex-wrap gap-2">
              {post.tags.map((tag, index) => (
                <Link 
                  key={index} 
                  to={`/blog?tag=${tag}`}
                  className="text-sm bg-gray-100 text-gray-600 px-3 py-1 rounded-full hover:bg-gray-200"
                >
                  {tag}
                </Link>
              ))}
            </div>
          </div>
          
          {/* Share */}
          <div className="mb-12">
            <h3 className="text-lg font-bold text-mypa-blue mb-3">{t("blog.share") || "Partager"}</h3>
            <div className="flex space-x-4">
              <a href="#" className="text-gray-500 hover:text-blue-600">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"></path>
                </svg>
              </a>
              <a href="#" className="text-gray-500 hover:text-blue-400">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723 10.054 10.054 0 01-3.127 1.184 4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"></path>
                </svg>
              </a>
              <a href="#" className="text-gray-500 hover:text-blue-500">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"></path>
                </svg>
              </a>
              <a href="#" className="text-gray-500 hover:text-green-500">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"></path>
                </svg>
              </a>
            </div>
          </div>
        </div>
        
        {/* Sidebar */}
        <div className="lg:col-span-1">
          {/* Related posts */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <h3 className="text-xl font-bold text-mypa-blue mb-4">{t("blog.related_posts") || "Articles similaires"}</h3>
            <div className="space-y-6">
              {getRelatedPosts().map((relatedPost: any) => (
                <div key={relatedPost.id} className="group">
                  <div className="h-40 bg-gray-200 rounded-lg mb-3 overflow-hidden">
                    {/* Placeholder for post image */}
                    <div className="w-full h-full flex items-center justify-center bg-mypa-blue bg-opacity-10 group-hover:bg-opacity-20 transition-all">
                      <span className="text-mypa-blue font-medium">{relatedPost.category}</span>
                    </div>
                  </div>
                  <h4 className="font-bold text-mypa-blue mb-1 group-hover:text-mypa-green transition-colors">
                    <Link to={`/blog/${relatedPost.id}`}>{relatedPost.title}</Link>
                  </h4>
                  <p className="text-sm text-gray-500 mb-2">{formatDate(relatedPost.date)}</p>
                  <Link
                    to={`/blog/${relatedPost.id}`}
                    className="text-sm text-mypa-green font-medium hover:underline"
                  >
                    {t("blog.read_more") || "Lire la suite"} →
                  </Link>
                </div>
              ))}
              
              {getRelatedPosts().length === 0 && (
                <p className="text-gray-500">Aucun article similaire disponible.</p>
              )}
            </div>
          </div>
          
          {/* Newsletter */}
          <div className="bg-mypa-blue bg-opacity-10 rounded-lg shadow-md p-6">
            <h3 className="text-xl font-bold text-mypa-blue mb-2">{t("blog.newsletter.title") || "Abonnez-vous à notre newsletter"}</h3>
            <p className="text-gray-600 mb-4">{t("blog.newsletter.description") || "Recevez nos derniers articles et actualités directement dans votre boîte mail"}</p>
            <form className="flex flex-col space-y-3">
              <input
                type="email"
                placeholder={t("blog.newsletter.placeholder") || "Votre adresse email"}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-mypa-green focus:border-transparent"
              />
              <button
                type="submit"
                className="bg-mypa-green text-white px-4 py-2 rounded-lg font-medium hover:bg-opacity-90 transition-colors"
              >
                {t("blog.newsletter.button") || "S'abonner"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPost;
